import { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegistry } from '../context/RegistryContext';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

type FormErrors = {
  [key: string]: string;
};

const Register = () => {
  const navigate = useNavigate();
  const { addServiceDog } = useRegistry();

  // Form state
  const [formData, setFormData] = useState({
    dogName: '',
    dogBreed: '',
    dogAge: '',
    dogPhoto: '',
    trainerLicenseNumber: '',
    ownerName: '',
    ownerAddress: '',
    ownerPhone: '',
    ownerEmail: '',
    serviceType: '',
    certificationFile: '',
    paymentComplete: false
  });

  const [paymentConfirmationNumber, setPaymentConfirmationNumber] = useState('');

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [verifyingTrainer, setVerifyingTrainer] = useState(false);
  const [trainerVerified, setTrainerVerified] = useState(false);

  // No longer needed - PayPal integration removed

  // Handle form field changes
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle photo upload
  const handlePhotoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size and type
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setErrors(prev => ({ ...prev, dogPhoto: 'Photo must be less than 5MB' }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, dogPhoto: 'File must be an image' }));
      return;
    }

    // Create file preview
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setPhotoPreview(event.target.result as string);
        setFormData(prev => ({ ...prev, dogPhoto: event.target?.result as string }));

        // Clear error
        if (errors.dogPhoto) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.dogPhoto;
            return newErrors;
          });
        }
      }
    };
    reader.readAsDataURL(file);
  };

  // Handle certification file upload
  const handleCertFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setErrors(prev => ({ ...prev, certificationFile: 'File must be less than 10MB' }));
      return;
    }

    // Check file type
    if (!file.type.match('application/pdf|image/jpeg|image/png')) {
      setErrors(prev => ({ ...prev, certificationFile: 'File must be PDF, JPEG, or PNG' }));
      return;
    }

    // Store file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setFormData(prev => ({ ...prev, certificationFile: event.target?.result as string }));

        // Clear error
        if (errors.certificationFile) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.certificationFile;
            return newErrors;
          });
        }
      }
    };
    reader.readAsDataURL(file);
  };

  // Validate step 1 fields
  const validateStep1 = () => {
    const newErrors: FormErrors = {};

    if (!formData.dogName.trim()) newErrors.dogName = 'Dog name is required';
    if (!formData.dogBreed.trim()) newErrors.dogBreed = 'Dog breed is required';
    if (!formData.dogAge.trim()) newErrors.dogAge = 'Dog age is required';
    if (!formData.dogPhoto) newErrors.dogPhoto = 'Dog photo is required';
    if (!formData.serviceType) newErrors.serviceType = 'Service type is required';

    if (!formData.trainerLicenseNumber.trim()) {
      newErrors.trainerLicenseNumber = 'Trainer license number is required';
    } else if (formData.trainerLicenseNumber !== 'NJ-330135') {
      newErrors.trainerLicenseNumber = 'Invalid trainer license number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate step 2 fields
  const validateStep2 = () => {
    const newErrors: FormErrors = {};

    if (!formData.ownerName.trim()) newErrors.ownerName = 'Owner name is required';
    if (!formData.ownerAddress.trim()) newErrors.ownerAddress = 'Address is required';
    if (!formData.ownerPhone.trim()) newErrors.ownerPhone = 'Phone number is required';
    if (!formData.ownerEmail.trim()) {
      newErrors.ownerEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.ownerEmail)) {
      newErrors.ownerEmail = 'Email is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Simulate trainer verification
  const verifyTrainer = () => {
    setVerifyingTrainer(true);

    // Simulate verification process with a 10-second delay
    setTimeout(() => {
      setVerifyingTrainer(false);
      setTrainerVerified(true);

      // No longer automatically proceeding to next step
      // User will need to close the modal manually
    }, 10000); // 10 seconds verification time
  };

  // Handle closing the verification success modal
  const closeVerificationModal = () => {
    setTrainerVerified(false);
    setStep(2);
    window.scrollTo(0, 0);
  };

  // Handle next step
  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      // Show trainer verification screen instead of immediately going to step 2
      verifyTrainer();
    } else if (step === 2 && validateStep2()) {
      setStep(3);
      window.scrollTo(0, 0);
    }
  };

  // Handle previous step
  const handlePrevStep = () => {
    if (step === 2) {
      setStep(1);
      // Reset trainer verification state when going back to step 1
      setTrainerVerified(false);
    } else if (step === 3) {
      setStep(2);
    }
    window.scrollTo(0, 0);
  };

  // Handle payment verification
  const handlePaymentVerification = () => {
    const validConfirmationNumber = '075829615';

    if (paymentConfirmationNumber.trim() === validConfirmationNumber) {
      setFormData(prev => ({ ...prev, paymentComplete: true }));
      // Clear any payment errors
      if (errors.payment) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.payment;
          return newErrors;
        });
      }
    } else {
      setErrors(prev => ({
        ...prev,
        payment: 'Invalid payment confirmation number. Please contact your trainer for the correct confirmation number.'
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (step === 1) {
      handleNextStep();
      return;
    }

    if (step === 2) {
      handleNextStep();
      return;
    }

    // For step 3 (payment verification), we need to check if payment is verified
    if (step === 3 && !formData.paymentComplete) {
      setErrors(prev => ({ ...prev, payment: 'Please verify your payment confirmation number to continue' }));
      return;
    }

    setLoading(true);

    try {
      // Add the service dog to the registry
      const newDog = await addServiceDog(formData);

      // Redirect to confirmation page
      navigate(`/confirmation/${newDog.id}`);
    } catch (error) {
      console.error('Error registering service dog:', error);
      setErrors(prev => ({ ...prev, form: 'An error occurred. Please try again.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {/* Trainer Verification Modal */}
      {verifyingTrainer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-8 rounded-lg shadow-lg max-w-md w-full relative">
            <div className="text-center">
              <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-primary-600 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-bold text-neutral-800 mb-1 sm:mb-2">Verifying Trainer Information</h3>
              <p className="text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base">
                Please wait, this can take a minute...
              </p>
              <div className="mt-3 sm:mt-4 h-8 sm:h-10"></div> {/* Spacer to match height of success modal */}
            </div>
          </div>
        </div>
      )}

      {/* Trainer Verification Success Modal */}
      {!verifyingTrainer && trainerVerified && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeVerificationModal} // Close when clicking outside
        >
          <div
            className="bg-white p-4 sm:p-8 rounded-lg shadow-lg max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <div className="text-center">
              <CheckCircle className="h-10 w-10 sm:h-12 sm:w-12 text-success-600 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-bold text-neutral-800 mb-1 sm:mb-2">Trainer Verified Successfully</h3>
              <p className="text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base">
                Your Trainer has been verified:<br />
                <span className="font-semibold">"Northstar K9 Elite"</span><br />
                License Number: NJ-330135<br />
                Federal ID: 09013-S-19117
              </p>
              <button
                type="button"
                onClick={closeVerificationModal}
                className="mt-3 sm:mt-4 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 sm:px-6 rounded-lg shadow transition-colors text-sm sm:text-base"
              >
                Continue to Owner Information
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-primary-600 text-white p-4 sm:p-6">
          <h1 className="text-xl sm:text-2xl font-bold">Register Your Service Dog</h1>
          <p className="text-primary-100 mt-1 sm:mt-2 text-sm sm:text-base">
            Complete this form to register your service dog in the official US Service Dog Registry.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex flex-wrap border-b border-neutral-200">
          <div
            className={`flex-1 py-2 sm:py-4 px-2 sm:px-6 text-center font-medium text-xs sm:text-sm ${step === 1 ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-600' : 'text-neutral-500'}`}
          >
            <span className="inline-block w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-600 text-white text-xs sm:text-sm mr-1 sm:mr-2">1</span>
            <span className="hidden xs:inline">Dog Information</span>
            <span className="xs:hidden">Dog Info</span>
          </div>
          <div
            className={`flex-1 py-2 sm:py-4 px-2 sm:px-6 text-center font-medium text-xs sm:text-sm ${step === 2 ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-600' : 'text-neutral-500'}`}
          >
            <span className="inline-block w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-600 text-white text-xs sm:text-sm mr-1 sm:mr-2">2</span>
            <span className="hidden xs:inline">Owner Information</span>
            <span className="xs:hidden">Owner Info</span>
          </div>
          <div
            className={`flex-1 py-2 sm:py-4 px-2 sm:px-6 text-center font-medium text-xs sm:text-sm ${step === 3 ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-600' : 'text-neutral-500'}`}
          >
            <span className="inline-block w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-600 text-white text-xs sm:text-sm mr-1 sm:mr-2">3</span>
            <span className="hidden xs:inline">Payment Verification</span>
            <span className="xs:hidden">Payment</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
          {/* Global error */}
          {errors.form && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-accent-100 text-accent-700 rounded-lg flex items-start text-sm">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{errors.form}</span>
            </div>
          )}

          {/* Step 1: Dog Information */}
          {step === 1 && (
            <div className="space-y-6 animate-slide-up">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="dogName" className="block text-sm font-medium text-neutral-700 mb-1">
                    Dog Name <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="dogName"
                    name="dogName"
                    value={formData.dogName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.dogName ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.dogName && <p className="mt-1 text-sm text-accent-600">{errors.dogName}</p>}
                </div>

                <div>
                  <label htmlFor="dogBreed" className="block text-sm font-medium text-neutral-700 mb-1">
                    Dog Breed <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="dogBreed"
                    name="dogBreed"
                    value={formData.dogBreed}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.dogBreed ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.dogBreed && <p className="mt-1 text-sm text-accent-600">{errors.dogBreed}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="dogAge" className="block text-sm font-medium text-neutral-700 mb-1">
                  Dog Age <span className="text-accent-600">*</span>
                </label>
                <input
                  type="text"
                  id="dogAge"
                  name="dogAge"
                  placeholder="e.g., 3 years"
                  value={formData.dogAge}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.dogAge ? 'border-accent-600' : 'border-neutral-300'}`}
                />
                {errors.dogAge && <p className="mt-1 text-sm text-accent-600">{errors.dogAge}</p>}
              </div>

              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-neutral-700 mb-1">
                  Service Dog Type <span className="text-accent-600">*</span>
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.serviceType ? 'border-accent-600' : 'border-neutral-300'}`}
                >
                  <option value="">Select Service Type</option>
                  <option value="Mobility Support">Mobility Support</option>
                  <option value="Guide Dog">Guide Dog (Visual Impairment)</option>
                  <option value="Hearing Dog">Hearing Dog</option>
                  <option value="Psychiatric Service Dog">Psychiatric Service Dog</option>
                  <option value="Medical Alert">Medical Alert/Response</option>
                  <option value="Autism Support">Autism Support</option>
                  <option value="Seizure Response">Seizure Response</option>
                  <option value="Other">Other (Please specify)</option>
                </select>
                {errors.serviceType && <p className="mt-1 text-sm text-accent-600">{errors.serviceType}</p>}
              </div>

              <div>
                <label htmlFor="trainerLicenseNumber" className="block text-sm font-medium text-neutral-700 mb-1">
                  Trainer License Number <span className="text-accent-600">*</span>
                </label>
                <input
                  type="text"
                  id="trainerLicenseNumber"
                  name="trainerLicenseNumber"
                  value={formData.trainerLicenseNumber}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.trainerLicenseNumber ? 'border-accent-600' : 'border-neutral-300'}`}
                  placeholder="Enter trainer license number"
                />
                {errors.trainerLicenseNumber && <p className="mt-1 text-sm text-accent-600">{errors.trainerLicenseNumber}</p>}
                <p className="mt-1 text-xs text-neutral-500">Enter the license number of the trainer who trained your service dog.</p>
              </div>

              <div>
                <label htmlFor="dogPhoto" className="block text-sm font-medium text-neutral-700 mb-1">
                  Dog Photo <span className="text-accent-600">*</span>
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    id="dogPhoto"
                    name="dogPhoto"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="dogPhoto"
                    className="cursor-pointer bg-primary-50 text-primary-700 border border-primary-300 hover:bg-primary-100 px-4 py-2 rounded-lg transition-colors"
                  >
                    Choose Photo
                  </label>

                  {photoPreview && (
                    <div className="relative">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="h-20 w-20 object-cover rounded-lg border border-neutral-300"
                      />
                    </div>
                  )}
                </div>
                {errors.dogPhoto && <p className="mt-1 text-sm text-accent-600">{errors.dogPhoto}</p>}
                <p className="mt-1 text-xs text-neutral-500">Upload a clear photo of your service dog. Max size: 5MB.</p>
              </div>

              <div>
                <label htmlFor="certificationFile" className="block text-sm font-medium text-neutral-700 mb-1">
                  Certification Documentation (Optional)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    id="certificationFile"
                    name="certificationFile"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleCertFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="certificationFile"
                    className="cursor-pointer bg-neutral-50 text-neutral-700 border border-neutral-300 hover:bg-neutral-100 px-4 py-2 rounded-lg transition-colors"
                  >
                    Upload Document
                  </label>

                  {formData.certificationFile && (
                    <span className="text-sm text-success-600">File uploaded successfully</span>
                  )}
                </div>
                {errors.certificationFile && <p className="mt-1 text-sm text-accent-600">{errors.certificationFile}</p>}
                <p className="mt-1 text-xs text-neutral-500">Upload any training or certification documents. PDF, JPEG, or PNG. Max size: 10MB.</p>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg shadow transition-colors"
                >
                  Next: Owner Information
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Owner Information */}
          {step === 2 && (
            <div className="space-y-6 animate-slide-up">
              <div>
                <label htmlFor="ownerName" className="block text-sm font-medium text-neutral-700 mb-1">
                  Owner Full Name <span className="text-accent-600">*</span>
                </label>
                <input
                  type="text"
                  id="ownerName"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerName ? 'border-accent-600' : 'border-neutral-300'}`}
                />
                {errors.ownerName && <p className="mt-1 text-sm text-accent-600">{errors.ownerName}</p>}
              </div>

              <div>
                <label htmlFor="ownerAddress" className="block text-sm font-medium text-neutral-700 mb-1">
                  Address <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="ownerAddress"
                  name="ownerAddress"
                  value={formData.ownerAddress}
                  onChange={handleChange}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerAddress ? 'border-accent-600' : 'border-neutral-300'}`}
                ></textarea>
                {errors.ownerAddress && <p className="mt-1 text-sm text-accent-600">{errors.ownerAddress}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="ownerPhone" className="block text-sm font-medium text-neutral-700 mb-1">
                    Phone Number <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="tel"
                    id="ownerPhone"
                    name="ownerPhone"
                    value={formData.ownerPhone}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerPhone ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.ownerPhone && <p className="mt-1 text-sm text-accent-600">{errors.ownerPhone}</p>}
                </div>

                <div>
                  <label htmlFor="ownerEmail" className="block text-sm font-medium text-neutral-700 mb-1">
                    Email Address <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="ownerEmail"
                    name="ownerEmail"
                    value={formData.ownerEmail}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerEmail ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.ownerEmail && <p className="mt-1 text-sm text-accent-600">{errors.ownerEmail}</p>}
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between mt-8 pt-4 border-t border-neutral-200">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="bg-white border border-primary-600 text-primary-600 hover:bg-primary-50 font-medium py-2 px-6 rounded-lg shadow-sm transition-colors mb-4 md:mb-0"
                >
                  Back to Dog Information
                </button>

                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={loading}
                  className="bg-accent-600 hover:bg-accent-700 text-white font-medium py-2 px-6 rounded-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Next: Payment Verification'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment Verification */}
          {step === 3 && (
            <div className="space-y-6 animate-slide-up">
              <div className="bg-primary-50 p-6 rounded-lg border border-primary-100">
                <h3 className="text-xl font-bold text-primary-800 mb-4">Payment Verification</h3>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-neutral-700">10-Year Service Dog Registration</span>
                  <span className="font-bold text-lg text-primary-800">$99.99</span>
                </div>
                <p className="text-sm text-neutral-600 mb-6">
                  Your registration is valid for 10 years and includes an official digital registry card and verification services.
                </p>

                {formData.paymentComplete ? (
                  <div className="bg-success-100 text-success-800 p-4 rounded-lg flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span>Payment verification completed successfully!</span>
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-lg border border-neutral-200">
                    <h4 className="font-medium text-neutral-800 mb-4">Enter Payment Confirmation Number</h4>
                    <div className="mb-6">
                      <p className="text-neutral-600 mb-4">
                        Please enter the payment confirmation number provided by your licensed trainer after completing your payment.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="paymentConfirmation" className="block text-sm font-medium text-neutral-700 mb-2">
                            Payment Confirmation Number
                          </label>
                          <input
                            type="text"
                            id="paymentConfirmation"
                            value={paymentConfirmationNumber}
                            onChange={(e) => setPaymentConfirmationNumber(e.target.value)}
                            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center font-mono text-lg"
                            placeholder="Enter confirmation number"
                            maxLength={20}
                          />
                        </div>
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={handlePaymentVerification}
                            disabled={!paymentConfirmationNumber.trim()}
                            className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Verify Payment
                          </button>
                        </div>
                      </div>
                    </div>
                    {errors.payment && (
                      <div className="bg-accent-50 border border-accent-200 text-accent-800 p-4 rounded-lg">
                        <p className="text-sm text-center font-medium">{errors.payment}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col md:flex-row justify-between mt-8 pt-4 border-t border-neutral-200">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="bg-white border border-primary-600 text-primary-600 hover:bg-primary-50 font-medium py-2 px-6 rounded-lg shadow-sm transition-colors mb-4 md:mb-0"
                >
                  Back to Owner Information
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-accent-600 hover:bg-accent-700 text-white font-medium py-2 px-6 rounded-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Complete Registration'}
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-neutral-50 border-t border-neutral-200 text-xs sm:text-sm text-neutral-600">
          <p>
            By registering your service dog, you certify that all information provided is accurate and that your dog meets the qualifications for a service animal under the Americans with Disabilities Act. The registration fee of $99.99 covers a 10-year registration period.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;